<template>
    <div>
        <v-toolbar elevation="1" color="secondary" outlined dense>
            <v-breadcrumbs large :items="[{text: 'Financial Accounting'}, {text: 'General Ledger'}, {text: 'Account Statement'}]"></v-breadcrumbs>
        </v-toolbar>
        <v-sheet elevation="1" class="mt-2">
            <v-toolbar flat dense color="secondary">
                <v-toolbar-title v-if="statementInfo">
                    {{statementInfo.displayName}}
                    <small class="grey--text">
                        {{statementInfo.from}} to {{statementInfo.to}}
                    </small>
                </v-toolbar-title>
                <v-toolbar-title v-else>
                    Account Statement
                </v-toolbar-title>
                <v-spacer></v-spacer>
                <v-checkbox
                    v-if="accountStatements.length && baseCurrency != accountCurrency && statementInfo.displayName != null"
                    hide-details
                    dense
                    v-model="inAccountCurrency"
                    :label="accountCurrency">
                </v-checkbox>
                <v-toolbar-items>
                    <v-tooltip  v-if="accountStatements.length" bottom="">
                        <template v-slot:activator="{on: tooltip}">
                            <v-btn  
                                @click="openAccountStatementPrintDialog"
                                v-on="{...tooltip}" 
                                class="mx-1" 
                                icon>
                                <v-icon>mdi-printer</v-icon>
                            </v-btn>
                        </template>
                        <span>{{ $t('print') }}</span>
                    </v-tooltip>
                    <v-menu 
                        absolute
                        offset-y
                        :close-on-content-click="false"
                        > 
                        <template v-slot:activator="{on, attrs}">
                            <v-btn icon v-bind="attrs" v-on="on">
                                <v-icon>mdi-magnify</v-icon>
                            </v-btn>
                        </template>
                        <v-sheet rounded class="pa-4" color="surface" width="300">
                            <v-form v-model="valid">
                                <v-row>
                                    <v-col cols="12"> 
                                        <p-account-search
                                            dense
                                            hide-details="auto"
                                            v-model="form.account_id"
                                            outlined
                                            backgroundColor="white"
                                            @input="resetStatement"
                                            @input:object="(object) => account = object"
                                        > </p-account-search>
                                    </v-col>
                                    <v-col cols="12">  
                                        <v-menu
                                            ref="menu"
                                            v-model="displayFrom"
                                            :close-on-content-click="false"
                                            transition="scale-transition"
                                            offset-y
                                            min-width="290px">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-text-field
                                                    hide-details="auto"
                                                    outlined
                                                    background-color="white"
                                                    v-model="form.from"
                                                    :rules="rules.from"
                                                    dense
                                                    :label="$t('date_from')"
                                                    append-icon="mdi-calendar"
                                                    readonly
                                                    v-bind="attrs"
                                                    v-on="on">
                                                </v-text-field>
                                            </template>
                                            <v-date-picker @change="checkDate()" @input="displayFrom = false" v-model="form.from" no-title scrollable>
                                            </v-date-picker>
                                        </v-menu>
                                    </v-col>
                                    <v-col cols="12">
                                        <v-menu
                                            ref="menu"
                                            v-model="displayTo"
                                            :close-on-content-click="false"
                                            transition="scale-transition"
                                            offset-y
                                            min-width="290px">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-text-field
                                                    hide-details="auto"
                                                    outlined
                                                    background-color="white"
                                                    v-model="form.to"
                                                    :rules="rules.to"
                                                    dense
                                                    :label="$t('date_to')"
                                                    append-icon="mdi-calendar"
                                                    readonly
                                                    v-bind="attrs"
                                                    v-on="on">
                                                </v-text-field>
                                            </template>
                                            <v-date-picker :min="form.from" @input="displayTo = false" v-model="form.to" no-title scrollable>
                                            </v-date-picker>
                                        </v-menu>
                                    </v-col>

                                    <v-col cols="12">
                                        <p-dimension-search
                                            hide-details="auto"
                                            outlined
                                            dense
                                            backgroundColor="white"
                                            v-model="form.dimension_id"
                                        ></p-dimension-search>
                                    </v-col>

                                    <v-col cols="12">
                                        <v-text-field
                                            hide-details="auto"
                                            outlined
                                            dense
                                            background-color="white"
                                            :label="$t('memo')"
                                            v-model="form.narration">
                                        </v-text-field>
                                    </v-col>

                                    <v-col cols="12">
                                        <v-btn  :loading="loading.data" block depressed color="primary" :disabled="!valid" @click="createdDataFetch">
                                            search
                                        </v-btn>
                                    </v-col>
                                </v-row>
                            </v-form>
                        </v-sheet>    
                    </v-menu>
                </v-toolbar-items>
            </v-toolbar>
            <div class="pa-4">
                <v-data-table
                    @dblclick:row="openReferenceDocument"
                    disable-pagination
                    hide-default-footer
                    dense
                    :headers="filterHeaders"
                    :loading="loading.data"
                    :items="accountStatements">

                    <template v-slot:[`item.transaction_type`]="{ item }">
                        {{ item.transaction_type }}
                    </template>

                    <!-- <template v-slot:[`item.narration`]="{ item }">
                        <p-table-width :row="item.narration" :length="50"></p-table-width>
                    </template> -->

                    <template v-slot:[`item.base_debit`]="{ item }">
                        <div class="text-right">
                            {{ isPositive(item.base_amount) ?  formatMoney(Math.abs(item.base_amount), item.currency_name ? item.currency_name+' ': '',2) : null }}
                        </div>
                    </template>

                    <template v-slot:[`item.base_credit`]="{ item }">
                        <div class="text-right">
                            {{ isNegative(item.base_amount) ?  formatMoney(Math.abs(item.base_amount), item.currency_name ? item.currency_name+' ': '',2) : null }}
                        </div>
                    </template>

                    <template v-slot:[`item.base_balance`]="{ item }">
                        <div class="text-right">
                            {{ formatMoney(item.base_balance, item.currency_name ? item.currency_name+' ': '',2) }}
                        </div>
                    </template>

                    <template v-slot:[`item.debit`]="{ item }">
                        <div class="text-right">
                            {{ isPositive(item.amount) ?  formatMoney(Math.abs(item.amount), item.currency_name ? item.currency_name+' ': '',2) : null }}
                        </div>
                    </template>

                    <template v-slot:[`item.credit`]="{ item }">
                        <div class="text-right">
                            {{ isNegative(item.amount)?  formatMoney(Math.abs(item.amount), item.currency_name ? item.currency_name+' ': '',2) : null }}
                        </div>
                    </template>

                    <template v-slot:[`item.balance`]="{ item }">
                        <div class="text-right">
                            {{ formatMoney(item.balance, item.currency_name ? item.currency_name+' ': '',2) }}
                        </div>
                    </template>

                </v-data-table>
            </div>
        </v-sheet>
        <AccountSearchDialog
            v-if="accountSearchDialogProps.display"
            :search="accountSearchDialogProps.search"
            @close="clsoeAccountSearchDialog"
            @submit="onSubmitAccountSearch"
            ref="accountSearchDialog"/>
        <retry-dialog
            ref="retryDialog"
            @submit="onRetryDialogSubmit">
        </retry-dialog>
        <journal-entry-display-dialog
            v-if="journalEntryDisplayDialogProps.display"
            v-model="journalEntryDisplayDialogProps.display"
            :id="journalEntryDisplayDialogProps.id"
            @close="closeJournalEntryDisplayDialog"
            ref="journalEntryDisplayDialog">
        </journal-entry-display-dialog>
        <account-statement-print-dialog 
            v-if="accountStatementProps.display"
            :accountStatements="accountStatementProps.accountStatements"
            :base-currency="baseCurrency"
            :account-currency="accountCurrency"
            :in-account-currency="inAccountCurrency"
            :form="form"
            @close="closeAccountStatementPrintDialog">
        </account-statement-print-dialog>
        <invoice-display-dialog
            ref="invoiceDisplayDialog">
        </invoice-display-dialog>
        <payment-display-dialog
            ref="paymentDisplayDialog">
        </payment-display-dialog>
    </div>
</template>
<script>
import AccountSearchDialog from '@/components/accounting/AccountSearchDialog';
import {formatMoney} from 'accounting';
import moment from 'moment';
import { mapActions, mapState } from 'vuex';
import RetryDialog from '../../components/common/RetryDialog.vue';
import JournalEntryDisplayDialog from '../../components/accounting/JournalEntryDisplayDialog.vue';
import AccountStatementPrintDialog from '../../components/accounting/AccountStatementPrintDialog.vue';
// import PTableWidth from '../common/PTableWidth.vue';
import InvoiceDisplayDialog from '../Sales/InvoiceDisplayDialog.vue';
import PaymentDisplayDialog from '../Sales/PaymentDisplayDialog.vue';
import PAccountSearch from "./../searchComponents/PAccountSearch";
import PDimensionSearch from "./../searchComponents/PDimensionSearch";

const PAYMENT = 'PMT';
const SALES_INVOICE = 'SINV';
const JOURNAL_VOUCHER = 'JV';

export default {
    name: "PStatements",
    props: {
        statement: Object,
        showButton: {
            type: Boolean,
            required: false
        }
    },
    components: {
        AccountSearchDialog,
        RetryDialog,
        JournalEntryDisplayDialog,
        AccountStatementPrintDialog,
        // PTableWidth,
        InvoiceDisplayDialog,
        PaymentDisplayDialog,
        PAccountSearch,
        PDimensionSearch
    },
    data() {
        return {
            statementInfo: null,
            valid: false,
            displayFrom: false,
            displayTo: false,
            search: null,
            openPrams: null,
            baseCurrency: 'SDG',
            account:null,
            form: {
                account_id: null,
                from: new moment().format("YYYY-MM-DD"),
                to: new moment().format("YYYY-MM-DD"),
                dimension: null,
                dimension_id: null,
                narration: null
            },
            rules: {
                account: [
                    v => !!v || this.$t('rules_required')
                ],
                from: [
                    v => !!v || this.$t('rules_required')
                ],
                to: [
                    v => !!v || this.$t('rules_required')
                ]
            },
            loading: {
                data: false,
                account: false,
                dimension: false
            },
            field: {
                account: {
                    message: '',
                    error: false
                },
                dimension: {
                    message: '',
                    error: false
                }
            },
            inAccountCurrency:false,
            accountSearchDialogProps: {
                display: false,
                search: null
            },
            accountStatements: [],
            journalEntryDisplayDialogProps: {
                display: false,
                id: null
            },
            accountStatementProps: {
                display: false,
                accountStatements: []
            },
            dimendionSearchDialogProps: {
                display: false,
                search: null
            }
        }
    },
    computed: {
        ...mapState('accounting/dimension', ["dimensions"]),
        ...mapState('accounting/activeAccount', ["activeAccounts"]),
        accountCurrency() {
            return this.account ? this.account.currency : '';
        },
        ExecelFields() {
            return {
                "Date": "transaction_date",
                // "Transaction #": 'transaction_id',
                "Reference #": 'reference_code',
                "Reference Type": "transaction_type",
                "Account": 'account_name',
                "Description": 'narration',
                "Debit": "debit",
                "Credit": "credit",
                "Balance": "balance",
                "Dimension": "dimension"
            }
        },
        headers(){
            return [
                { text: this.$t('date'), sortable: true, value: 'transaction_date', show: true },
                // { text: this.$t('transaction'), sortable: false, value: 'transaction_id', show: true },
                { text: this.$t('reference_no'), sortable: false, value: 'reference_code', show: true },
                { text: this.$t('reference_type'), sortable: false, value: 'transaction_type', show: true },
                { text: this.$t('account'), sortable: false, value: 'account_name', show: false },
                { text: this.$t("description"), sortable: false, value: 'narration', show: true },
                { text: this.$t('Debit') + " ("+this.baseCurrency+") ", align: 'center', sortable: false, value: 'base_debit', show: !this.inAccountCurrency },
                { text: this.$t('Credit') + " ("+this.baseCurrency+") ", align: 'center', sortable: false, value: 'base_credit', show: !this.inAccountCurrency },
                { text: this.$t('Balance') + " ("+this.baseCurrency+") ", align: 'center', sortable: false, value: 'base_balance', show: !this.inAccountCurrency },
                { text: this.$t('debit') + " (" +this.accountCurrency+ ") ", align: 'center', sortable: false, value: 'debit', show: this.inAccountCurrency },
                { text: this.$t('credit') + " (" +this.accountCurrency+ ") ", align: 'center', sortable: false, value: 'credit', show: this.inAccountCurrency },
                { text: this.$t('balance') + " (" +this.accountCurrency+ ") ", align: 'center', sortable: false, value: 'balance', show: this.inAccountCurrency },
                { text: this.$t('dimension'), sortable: false, value: 'dimension_name', show: true },
            ]
        },
        filterHeaders() {
            console.log('Filter header.');
            return this.headers.filter(each => each.show == true);
        },
        execelDate() {
            return 'Date From: '+ this.form.from +'- Date To: '+ this.form.to; 
        },
        execelAccountName() {
            if(this.form.account) return 'Account Name: '+ this.form.account;
            else return null;
        },
        totalDebit() {
            // return this.accountStatements;
            return this._.sumBy(this.accountStatements,  (line) => {
                console.log('credit', line.debit);
                return Number(line.debit * 1e12 /1e12);
            });
        },

        // totalCredit() {
        //     return this.accountStatements;
        //     //  return this._.sumBy(this.accountStatements,  (line) => {
        //     //     return Number(line.credit * 1e12 /1e12);
        //     // });
        // }
    },
    methods: {
        formatMoney,
        ...mapActions('accounting/statement', ["fetchAccountStatements"]),
        ...mapActions('accounting/dimension', ["fetchDimensions"]),
        ...mapActions('accounting/activeAccount', ["fetchActiveAccounts", "fetchActiveAccount"]),
       async open() {
            if(this.statement) {
                this.form = this.statement;
                await Promise.all([this.createdDataFetch(), this.fetchActiveAccount(this.statement.account_id)]);
            }
        },
        async createdDataFetch() {
            try {
                this.clearData();
                this.loading.data = true;
                let result =  await this.fetchAccountStatements(this.form);
                this.accountStatements = result.data.data;
                this.statementInfo = result.data.info;
                this.loading.data = false;
            } catch(error) {
                this.loading.data = false;
                let message = error.response ? error.response.data.message : error;
                this.$refs.retryDialog.open({target:"createdDataFetch",message});
            }
        },
        isNegative(value)
        {
            return value < 0;
        },
        isPositive(value)
        {
            return value > 0;
        },
        clearData() {
            this.accountStatements = [];
        },
        checkDate() {
            if(this.form.from > this.form.to) this.form.to = this.form.from;
        },
        async fetchDimensionsData(val) {
            this.loading.dimension = true;
            this.clearFieldMessageDimension();
            let result = await this.fetchDimensions({ parent_id: 1, search: val });
            if(result.data.length == 0) this.setMessageDimension("Your search didn't match any record!");
            this.loading.dimension = false;
        },
        setMessageDimension(val) {
            this.field.dimension.error = true;
            this.field.dimension.message = val;
        },
        clearFieldMessageDimension() {
            this.field.dimension.error = false;
            this.field.dimension.message = '';
        },
        openAccountSearchDialog(item) {
            this.accountSearchDialogProps.search = item;
            this.accountSearchDialogProps.display = true;
        },
        clsoeAccountSearchDialog() {
            this.accountSearchDialogProps.display = false;
        },
        onSubmitAccountSearch(params) {
            this.form.account = params.selected.displayName;
            this.form.account_id = params.selected.id;
            this.$emit('onSubmitAccountSearch');
        },
        openReferenceDocument(e, line) {
            switch (line.item.reference_abreviation) {
                case JOURNAL_VOUCHER:
                    this.openJournalEntryDisplayDialog(line.item.reference_id);
                break;
                case SALES_INVOICE:
                    this.openInvoiceDisplayDialog(line.item.reference_id);
                break;
                case PAYMENT:
                    this.openPaymentDisplayDialog(line.item.reference_id);
                break;
                default : 
            }
        },
        openJournalEntryDisplayDialog(id) {
            this.journalEntryDisplayDialogProps.id = id;
            this.journalEntryDisplayDialogProps.display= true;
        },
        openInvoiceDisplayDialog(id) {
            this.$refs.invoiceDisplayDialog.open({id});
        },
        openPaymentDisplayDialog(id) {
            this.$refs.paymentDisplayDialog.open({id});
        },
        closeJournalEntryDisplayDialog() {
            this.journalEntryDisplayDialogProps.display = false;
        },
        openAccountStatementPrintDialog() {
            if(this.form.account_id) this.form.account = this.account.displayName;
            this.accountStatementProps.accountStatements = this.accountStatements;
            this.accountStatementProps.display = true;
        }, 
        closeAccountStatementPrintDialog() {
            this.accountStatementProps.display = false;
        },
        openDimensionSearchDialog(dimension) {
            this.dimendionSearchDialogProps.search = dimension;
            this.dimendionSearchDialogProps.display = true;
        },
        closeDimensionSearchDialog() {
            this.dimendionSearchDialogProps.display = false;
        },
        onSubmitDimensionSearch(params) {
            this.form.dimension = params.selected.name;
            this.form.dimension_id = params.selected.id;
            this.$emit('onSubmitDimensionSearch');
        },
        onRetryDialogSubmit(params){
            if(params.target == "createdDataFetch") {
                if(params.retry) this.createdDataFetch();
            }
        },
        close() {
            this.$emit('close');
        },
        resetStatement() {
            this.accountStatements = [];
            this.statementInfo = null;
        }
    },
    created() {
        this.open();
    },
    updated() {
        this.$on('onSubmitAccountSearch', () => { 
            this.clsoeAccountSearchDialog();
            this.clearData();
        });
        this.$on('onSubmitDimensionSearch', () => {
            this.closeDimensionSearchDialog();
        });
    }
}
</script>